import React, { useMemo, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, MenuItem, Select, Step, StepLabel,
  Stepper, TextField, Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  agreementCanCorrect, agreementHasActiveInstallments, agreementStepError,
  paymentPlanDifference, updateAgreementKind,
} from './commercialCommitmentState';
import { formatCurrency, formatDate, formatMoney } from './commercialFormatters';
import { toLocalDateInputValue } from './commercialDateTime';

const today = () => toLocalDateInputValue();
const dateValue = value => String(value || '').slice(0, 10);
const steps = ['Terms and dates', 'Value and MRR', 'Billing and review'];

const initialForm = ({ agreement, customer, initialKind, renewalId }) => agreement ? {
  customerId: agreement.customerId || customer.id,
  renewalId: agreement.renewalId || null,
  kind: agreement.kind || initialKind,
  status: agreement.status || 'AGREED',
  contractAlignment: agreement.contractAlignment || 'ANNIVERSARY',
  fiscalYearStartMonth: agreement.fiscalYearStartMonth || '',
  agreedAt: dateValue(agreement.agreedAt),
  serviceStartsAt: dateValue(agreement.serviceStartsAt),
  serviceEndsAt: dateValue(agreement.serviceEndsAt),
  billingStartsAt: dateValue(agreement.billingStartsAt),
  billingEndsAt: dateValue(agreement.billingEndsAt),
  recognitionDate: dateValue(agreement.recognitionDate),
  totalAmount: Number(agreement.totalAmountMinor || 0) / 100,
  previousMrr: Number(agreement.previousMrr || 0),
  resultingMrr: Number(agreement.resultingMrr || 0),
  termMonths: Number(agreement.termMonths || 12),
  currency: agreement.currency || 'EUR',
  notes: agreement.notes || '',
  featureIds: (agreement.features || []).map(feature => feature.id || feature.featureId || feature),
  installments: (agreement.installments || []).map(item => ({
    ...item,
    dueDate: dateValue(item.dueDate),
    amount: Number(item.amountMinor || 0) / 100,
  })),
} : {
  customerId: customer.id,
  renewalId,
  kind: initialKind,
  status: 'AGREED',
  contractAlignment: 'ANNIVERSARY',
  fiscalYearStartMonth: '',
  agreedAt: today(),
  serviceStartsAt: renewalId ? customer.renewalDate || today() : today(),
  serviceEndsAt: '',
  billingStartsAt: renewalId ? customer.renewalDate || today() : today(),
  billingEndsAt: '',
  recognitionDate: renewalId ? customer.renewalDate || today() : today(),
  totalAmount: renewalId ? Number(customer.arrAtRisk || 0) : 0,
  previousMrr: Number(customer.currentMrr || 0),
  resultingMrr: Number(customer.currentMrr || 0),
  termMonths: 12,
  currency: 'EUR',
  notes: '',
  featureIds: [],
  installments: [],
};

export default function CommercialCommitmentDialog({
  customer,
  featureCatalog = [],
  headers,
  hostname,
  initialKind = 'RENEWAL',
  renewalId = null,
  agreement = null,
  onClose,
  onSaved,
}) {
  const editMode = Boolean(agreement);
  const correctable = editMode && agreementCanCorrect(agreement);
  const valueLocked = editMode && agreementHasActiveInstallments(agreement);
  const [form, setForm] = useState(() => initialForm({ agreement, customer, initialKind, renewalId }));
  const [step, setStep] = useState(0);
  const [stepError, setStepError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const installmentTotal = useMemo(
    () => form.installments.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    [form.installments],
  );
  const paymentDifference = paymentPlanDifference(form);
  const paymentDifferenceValid = Number.isFinite(paymentDifference);

  const update = patch => {
    setForm(current => ({ ...current, ...patch }));
    setStepError(null);
  };
  const updateInstallment = (index, field, value) => {
    setForm(current => ({
      ...current,
      installments: current.installments.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [field]: value } : item
      )),
    }));
    setStepError(null);
  };
  const continueToNextStep = () => {
    const issue = agreementStepError(form, step);
    if (issue) {
      setStepError(issue);
      return;
    }
    setStepError(null);
    setStep(current => current + 1);
  };
  const save = async () => {
    if (editMode && !correctable) return;
    const validationSteps = editMode ? [0, 1] : [0, 1, 2];
    const invalidStep = validationSteps.find(index => agreementStepError(form, index));
    if (invalidStep !== undefined) {
      setStep(invalidStep);
      setStepError(agreementStepError(form, invalidStep));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { installments, ...agreementFields } = form;
      const response = await fetch(`${hostname}/commercial/commitments${editMode ? `/${agreement.id}` : ''}`, {
        method: editMode ? 'PATCH' : 'POST',
        headers,
        body: JSON.stringify(editMode ? agreementFields : { ...agreementFields, installments }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || `Could not save agreement (${response.status})`);
      onSaved(result);
    } catch (cause) {
      setError(cause.message);
    } finally {
      setSaving(false);
    }
  };
  const cancelAgreement = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      const response = await fetch(`${hostname}/commercial/commitments/${agreement.id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ status: 'CANCELLED' }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || `Could not cancel agreement (${response.status})`);
      onSaved(result);
    } catch (cause) {
      setCancelError(cause.message);
    } finally {
      setCancelling(false);
    }
  };

  const kindLabel = form.kind === 'RENEWAL'
    ? 'Renewal'
    : form.kind === 'EXPANSION' ? 'Expansion' : 'New contract';

  return <>
    <Dialog open fullWidth maxWidth="md" onClose={() => !saving && !cancelling && onClose()}>
      <DialogTitle>{editMode ? 'Review agreement' : 'Plan agreement'} · {kindLabel}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary">
          {editMode
            ? 'Correct planned terms before recognition. Cash collection stays in Payments, and every saved change remains in the audit history.'
            : 'Save terms first. Cash collection in Payments and Apply to MRR remain separate actions.'}
        </Typography>
        {editMode && !correctable && <Box mt={2}>
          <Typography color="textSecondary">This agreement is locked because it has been cancelled or applied to MRR.</Typography>
        </Box>}
        <Stepper activeStep={step} alternativeLabel style={{ paddingLeft: 0, paddingRight: 0 }}>
          {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        {step === 0 && <Box display="grid" gridGap={16} style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))' }}>
          <FormControl variant="outlined">
            <InputLabel>Agreement type</InputLabel>
            <Select disabled={editMode} label="Agreement type" value={form.kind} onChange={event => {
              setForm(current => updateAgreementKind(current, event.target.value, renewalId));
              setStepError(null);
            }}>
              <MenuItem value="NEW">New contract</MenuItem>
              <MenuItem value="RENEWAL">Renewal</MenuItem>
              <MenuItem value="EXPANSION">Expansion</MenuItem>
            </Select>
            {editMode && <Typography variant="caption" color="textSecondary">Agreement type is fixed after creation.</Typography>}
          </FormControl>
          <FormControl variant="outlined">
            <InputLabel>Contract alignment</InputLabel>
            <Select label="Contract alignment" value={form.contractAlignment} onChange={event => update({ contractAlignment: event.target.value })}>
              <MenuItem value="ANNIVERSARY">Contract anniversary</MenuItem>
              <MenuItem value="CALENDAR_YEAR">Calendar year</MenuItem>
              <MenuItem value="FISCAL_YEAR">Fiscal year</MenuItem>
              <MenuItem value="CUSTOM">Custom dates</MenuItem>
            </Select>
          </FormControl>
          {form.contractAlignment === 'FISCAL_YEAR' && <TextField variant="outlined" type="number" inputProps={{ min: 1, max: 12 }} label="Fiscal year starts in month" value={form.fiscalYearStartMonth} onChange={event => update({ fiscalYearStartMonth: event.target.value })} helperText="1 = January, 4 = April" />}
          <TextField variant="outlined" type="date" InputLabelProps={{ shrink: true }} inputProps={{ 'aria-label': 'Agreement date' }} label="Agreement date" value={form.agreedAt} onChange={event => update({ agreedAt: event.target.value })} />
          <TextField required variant="outlined" type="date" InputLabelProps={{ shrink: true }} inputProps={{ 'aria-label': 'Service starts' }} label="Service starts" value={form.serviceStartsAt} onChange={event => update({ serviceStartsAt: event.target.value })} />
          <TextField variant="outlined" type="date" InputLabelProps={{ shrink: true }} label="Service ends" value={form.serviceEndsAt} onChange={event => update({ serviceEndsAt: event.target.value })} />
          <TextField variant="outlined" type="date" InputLabelProps={{ shrink: true }} inputProps={{ 'aria-label': 'Billing starts' }} label="Billing starts" value={form.billingStartsAt} onChange={event => update({ billingStartsAt: event.target.value })} />
          <TextField variant="outlined" type="date" InputLabelProps={{ shrink: true }} label="Billing ends" value={form.billingEndsAt} onChange={event => update({ billingEndsAt: event.target.value })} />
          <TextField variant="outlined" type="number" inputProps={{ min: 1 }} label="Term (months)" value={form.termMonths} onChange={event => update({ termMonths: event.target.value })} />
        </Box>}

        {step === 1 && <Box display="grid" gridGap={16} style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))' }}>
          <TextField required variant="outlined" type="date" InputLabelProps={{ shrink: true }} label="MRR recognition date" value={form.recognitionDate} onChange={event => update({ recognitionDate: event.target.value })} helperText="MRR will not change until Apply to MRR is confirmed." />
          <TextField id="commercial-total-contract-value" disabled={valueLocked} variant="outlined" type="number" inputProps={{ min: 0, step: .01 }} label="Total contract value" value={form.totalAmount} onChange={event => update({ totalAmount: event.target.value })} helperText={valueLocked ? 'Payment stages already exist; cancel and replace the agreement or edit payment stages separately.' : undefined} />
          <TextField disabled={valueLocked} variant="outlined" label="Contract currency" inputProps={{ maxLength: 3, 'aria-label': 'Contract currency' }} value={form.currency} onChange={event => update({ currency: event.target.value.toUpperCase() })} helperText={valueLocked ? 'Currency is locked once payment stages exist.' : 'Three-letter ISO code, for example EUR, USD, or GBP.'} />
          <TextField variant="outlined" type="number" inputProps={{ min: 0, step: .01 }} label="Current MRR" value={form.previousMrr} onChange={event => update({ previousMrr: event.target.value })} />
          <TextField variant="outlined" type="number" inputProps={{ min: 0, step: .01 }} label="MRR after this agreement" value={form.resultingMrr} onChange={event => update({ resultingMrr: event.target.value })} helperText={form.kind === 'RENEWAL' ? 'May stay unchanged for a like-for-like renewal.' : 'Set the recurring amount after this agreement.'} />
          <FormControl variant="outlined">
            <InputLabel>Features included</InputLabel>
            <Select multiple label="Features included" value={form.featureIds} onChange={event => update({ featureIds: event.target.value })} renderValue={selected => selected.map(id => featureCatalog.find(feature => feature.id === id)?.name || id).join(', ')}>
              {featureCatalog.map(feature => <MenuItem key={feature.id} value={feature.id}>{feature.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>}

        {step === 2 && <>
          <CardReview form={form} featureCatalog={featureCatalog} />
          <Box mt={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={8}>
              <div>
                <Typography variant="subtitle1">Payment plan</Typography>
                <Typography variant="caption" color="textSecondary">
                  {editMode ? 'Payment stages are shown for context; receipts and balances are managed in Payments.' : 'Leave empty to record receipts later in Payments. Added stages must equal the contract value.'}
                </Typography>
              </div>
              {!editMode && <Button onClick={() => {
                setForm(current => ({ ...current, installments: [...current.installments, { dueDate: current.billingStartsAt || today(), amount: '' }] }));
                setStepError(null);
              }}>Add stage</Button>}
            </Box>
            {editMode
              ? <PaymentPlanReview installments={form.installments} currency={form.currency} />
              : form.installments.map((item, index) => <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={12} mt={1} key={index}>
                <TextField type="date" InputLabelProps={{ shrink: true }} variant="outlined" size="small" label={`Stage ${index + 1} due`} value={item.dueDate} onChange={event => updateInstallment(index, 'dueDate', event.target.value)} />
                <TextField type="number" inputProps={{ min: .01, step: .01 }} variant="outlined" size="small" label="Amount" value={item.amount} onChange={event => updateInstallment(index, 'amount', event.target.value)} />
                <IconButton aria-label={`Remove payment stage ${index + 1}`} onClick={() => {
                  setForm(current => ({ ...current, installments: current.installments.filter((_, itemIndex) => itemIndex !== index) }));
                  setStepError(null);
                }}><DeleteIcon /></IconButton>
              </Box>)}
            {!editMode && form.installments.length > 0 && <Box mt={1}>
              <Typography variant="body2" color={!paymentDifferenceValid || paymentDifference !== 0 ? 'error' : 'textSecondary'}>Stages: {formatCurrency(installmentTotal, form.currency)} · Contract: {formatCurrency(Number(form.totalAmount || 0), form.currency)}</Typography>
              {!paymentDifferenceValid
                ? <Typography variant="caption" color="error">Use no more than two decimal places in every payment stage.</Typography>
                : paymentDifference !== 0 && <Typography variant="caption" color="error">{paymentDifference > 0 ? `${formatCurrency(paymentDifference, form.currency)} remaining to allocate.` : `${formatCurrency(Math.abs(paymentDifference), form.currency)} exceeds the contract value.`}</Typography>}
            </Box>}
          </Box>
          <Box mt={2}><TextField fullWidth multiline minRows={3} variant="outlined" label="Commercial notes" value={form.notes} onChange={event => update({ notes: event.target.value })} /></Box>
        </>}

        {stepError && <Box mt={2}><Typography color="error" role="alert">{stepError}</Typography></Box>}
        {error && <Box mt={2}><Typography color="error" role="alert">{error}</Typography></Box>}
      </DialogContent>
      <DialogActions>
        {correctable && <Button disabled={saving || cancelling} color="secondary" onClick={() => {
          setCancelError(null);
          setCancelOpen(true);
        }}>Cancel agreement</Button>}
        <Box flexGrow={1} />
        <Button disabled={saving || cancelling} onClick={onClose}>{editMode ? 'Close' : 'Cancel'}</Button>
        {step > 0 && <Button disabled={saving || cancelling} onClick={() => {
          setStepError(null);
          setStep(current => current - 1);
        }}>Back</Button>}
        {step < steps.length - 1
          ? <Button color="primary" variant="contained" disabled={saving || cancelling} onClick={continueToNextStep}>Continue</Button>
          : (!editMode || correctable) && <Button disabled={saving || cancelling} color="primary" variant="contained" onClick={save}>{saving ? 'Saving…' : editMode ? 'Save changes' : 'Save agreement'}</Button>}
      </DialogActions>
    </Dialog>

    {cancelOpen && <Dialog open fullWidth maxWidth="sm" onClose={() => !cancelling && setCancelOpen(false)}>
      <DialogTitle>Cancel this agreement?</DialogTitle>
      <DialogContent>
        <Typography paragraph><strong>{customer.name || agreement.customerName}</strong> · {kindLabel}</Typography>
        <Typography variant="body2" color="textSecondary">This stops further payment and MRR actions. Existing payment records and audit history are retained.</Typography>
        {cancelError && <Box mt={2}><Typography color="error" role="alert">{cancelError}</Typography></Box>}
      </DialogContent>
      <DialogActions>
        <Button disabled={cancelling} onClick={() => setCancelOpen(false)}>Keep agreement</Button>
        <Button disabled={cancelling} color="secondary" variant="contained" onClick={cancelAgreement}>{cancelling ? 'Cancelling…' : 'Confirm cancellation'}</Button>
      </DialogActions>
    </Dialog>}
  </>;
}

function PaymentPlanReview({ installments, currency }) {
  if (!installments.length) return <Box mt={1}><Typography variant="body2" color="textSecondary">No payment stages recorded. Record receipts from Payments when cash is received.</Typography></Box>;
  return <Box mt={1}>
    {installments.map((item, index) => <Box key={item.id || index} py={1} borderBottom={1} borderColor="divider" display="flex" justifyContent="space-between" flexWrap="wrap" gridGap={8}>
      <Typography variant="body2">Stage {index + 1} · due {formatDate(item.dueDate)}</Typography>
      <Typography variant="body2">{formatCurrency(Number(item.paidAmountMinor || 0) / 100, currency)} paid of {formatCurrency(item.amount, currency)} · {String(item.status || 'UNPAID').replace(/_/g, ' ')}</Typography>
    </Box>)}
  </Box>;
}

function CardReview({ form, featureCatalog }) {
  const selectedFeatures = form.featureIds.map(id => featureCatalog.find(feature => feature.id === id)?.name || id);
  return <Box bgcolor="action.hover" borderRadius={4} p={2}>
    <Typography variant="subtitle1">Review agreement</Typography>
    <Typography variant="body2">{form.kind.replace(/_/g, ' ')} · {formatDate(form.serviceStartsAt)}{form.serviceEndsAt ? ` – ${formatDate(form.serviceEndsAt)}` : ''} · {form.termMonths} months</Typography>
    <Typography variant="body2">Value {formatCurrency(Number(form.totalAmount || 0), form.currency)} · MRR {formatMoney(Number(form.previousMrr || 0))} → {formatMoney(Number(form.resultingMrr || 0))}</Typography>
    <Typography variant="caption" color="textSecondary">Apply to MRR on {formatDate(form.recognitionDate)} independently from payment collection.</Typography>
    <Box mt={1}>{selectedFeatures.length
      ? selectedFeatures.map(feature => <Chip key={feature} size="small" label={feature} style={{ marginRight: 4 }} />)
      : <Typography variant="caption" color="textSecondary">No features selected</Typography>}</Box>
  </Box>;
}
